/**
 * VaultDuplicateModal: Shown when uploading files that already exist
 * in the target folder. Lets user choose per-file or bulk: Replace, Keep Both, Skip.
 */
import { useState, useEffect } from 'react';
import {
  X,
  AlertTriangle,
  ArrowRightLeft,
  Copy,
  SkipForward,
  FileText,
  Upload,
  Shield,
} from 'lucide-react';
import type { VaultFile } from '../../types/vault.types';
import { formatFileSize } from '../../types/vault.types';

export type DuplicateAction = 'replace' | 'keep-both' | 'skip';

export interface DuplicateEntry {
  /** The existing file already in the target folder */
  existing: VaultFile;
  /** The new file (from upload) or file being moved */
  incoming: File | VaultFile;
  /** User's chosen action for this pair */
  action: DuplicateAction;
}

interface VaultDuplicateModalProps {
  isOpen: boolean;
  entries: DuplicateEntry[];
  onConfirm: (entries: DuplicateEntry[]) => void;
  onCancel: () => void;
  /** Optional: non-duplicate files that will upload regardless */
  nonDuplicateCount?: number;
}

export function VaultDuplicateModal({
  isOpen,
  entries: initialEntries,
  onConfirm,
  onCancel,
  nonDuplicateCount = 0,
}: VaultDuplicateModalProps) {
  const [entries, setEntries] = useState<DuplicateEntry[]>(initialEntries);

  // CRITICAL: Sync local state when initialEntries changes (re-opening modal)
  useEffect(() => {
    setEntries(initialEntries);
  }, [initialEntries]);

  if (!isOpen || entries.length === 0) return null;

  const setAction = (index: number, action: DuplicateAction) => {
    setEntries(prev => prev.map((e, i) => i === index ? { ...e, action } : e));
  };

  const setBulkAction = (action: DuplicateAction) => {
    setEntries(prev => prev.map(e => ({ ...e, action })));
  };

  const getIncomingSize = (incoming: File | VaultFile): number => {
    if (incoming instanceof File) return incoming.size;
    return (incoming as VaultFile).size_bytes;
  };

  const getIncomingName = (incoming: File | VaultFile): string => {
    if (incoming instanceof File) return incoming.name;
    return (incoming as VaultFile).name;
  };

  // Calculate summary
  const replaceCount = entries.filter(e => e.action === 'replace').length;
  const keepCount = entries.filter(e => e.action === 'keep-both').length;
  const skipCount = entries.filter(e => e.action === 'skip').length;
  const hasAnyAction = replaceCount > 0 || keepCount > 0;

  return (
    <div className="vault-modal-overlay" onClick={onCancel}>
      <div
        className="vault-modal vault-duplicate-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="vault-dup-header">
          <div className="vault-dup-header-icon">
            <AlertTriangle size={20} />
          </div>
          <div className="vault-dup-header-text">
            <h3>{entries.length} Duplicate{entries.length > 1 ? 's' : ''} Found</h3>
            <p>
              {entries.length === 1
                ? 'A file with this name already exists in the destination.'
                : `${entries.length} files already exist in the destination.`}
            </p>
          </div>
          <button className="vault-modal-close" onClick={onCancel}>
            <X size={18} />
          </button>
        </div>

        {/* Bulk actions */}
        {entries.length > 1 && (
          <div className="vault-dup-bulk">
            <span className="vault-dup-bulk-label">Quick actions:</span>
            <div className="vault-dup-bulk-group">
              <button
                className={`vault-dup-bulk-btn ${entries.every(e => e.action === 'replace') ? 'active replace' : ''}`}
                onClick={() => setBulkAction('replace')}
              >
                <ArrowRightLeft size={12} /> Replace All
              </button>
              <button
                className={`vault-dup-bulk-btn ${entries.every(e => e.action === 'keep-both') ? 'active keep' : ''}`}
                onClick={() => setBulkAction('keep-both')}
              >
                <Copy size={12} /> Keep All
              </button>
              <button
                className={`vault-dup-bulk-btn ${entries.every(e => e.action === 'skip') ? 'active skip' : ''}`}
                onClick={() => setBulkAction('skip')}
              >
                <SkipForward size={12} /> Skip All
              </button>
            </div>
          </div>
        )}

        {/* File list */}
        <div className="vault-dup-list">
          {entries.map((entry, idx) => (
            <div key={entry.existing.id} className="vault-dup-row">
              <div className="vault-dup-file-icon">
                <FileText size={20} />
              </div>
              <div className="vault-dup-file-info">
                <span className="vault-dup-file-name">{getIncomingName(entry.incoming)}</span>
                <span className="vault-dup-file-meta">
                  Existing: {formatFileSize(entry.existing.size_bytes)} · New: {formatFileSize(getIncomingSize(entry.incoming))}
                </span>
              </div>
              <div className="vault-dup-file-actions">
                <button
                  className={`vault-dup-action ${entry.action === 'replace' ? 'active replace' : ''}`}
                  onClick={() => setAction(idx, 'replace')}
                  title="Replace existing file with new version"
                >
                  <ArrowRightLeft size={12} />
                  <span>Replace</span>
                </button>
                <button
                  className={`vault-dup-action ${entry.action === 'keep-both' ? 'active keep' : ''}`}
                  onClick={() => setAction(idx, 'keep-both')}
                  title="Keep both, the new file will be renamed"
                >
                  <Copy size={12} />
                  <span>Keep Both</span>
                </button>
                <button
                  className={`vault-dup-action ${entry.action === 'skip' ? 'active skip' : ''}`}
                  onClick={() => setAction(idx, 'skip')}
                  title="Don't upload this file"
                >
                  <SkipForward size={12} />
                  <span>Skip</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Summary bar */}
        {(nonDuplicateCount > 0 || hasAnyAction) && (
          <div className="vault-dup-summary">
            {replaceCount > 0 && (
              <span className="vault-dup-summary-tag replace">
                <ArrowRightLeft size={11} /> {replaceCount} replace
              </span>
            )}
            {keepCount > 0 && (
              <span className="vault-dup-summary-tag keep">
                <Copy size={11} /> {keepCount} keep both
              </span>
            )}
            {skipCount > 0 && (
              <span className="vault-dup-summary-tag skip">
                <SkipForward size={11} /> {skipCount} skip
              </span>
            )}
            {nonDuplicateCount > 0 && (
              <span className="vault-dup-summary-tag new">
                <Upload size={11} /> {nonDuplicateCount} new
              </span>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="vault-dup-footer">
          {hasAnyAction || nonDuplicateCount > 0 ? (
            <>
              <button className="vault-dup-cancel" onClick={onCancel}>
                Cancel
              </button>
              <button
                className="vault-dup-confirm show"
                onClick={() => onConfirm(entries)}
              >
                <Shield size={14} />
                Continue Upload
              </button>
            </>
          ) : (
            <button className="vault-dup-cancel solo" onClick={onCancel}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
